import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../LoopinoDesktopGuidelines.css';
import './DesktopBatteryPage.css';
import { API_CONFIG } from '../../../config/api';
import { useCapabilities } from '../../../capabilities/CapabilityContext';
import { Skeleton } from '../../effects';
import useLanguage from '../../../hooks/useLanguage';
import Iridescence from '../../Mobile/MobileBatteryPanel/Iridescence';
import { apiFetch, parseResponseBody } from '../../../utils/http';
import iconAi from '../../../assets/images/icons/icon-ai.svg';
import iconCalendar from '../../../assets/images/icons/icon-calendar.svg';

const WEEK_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

const toNumber = (value, fallback = 0) => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseAPITimestamp = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    const asMilliseconds = value > 1e12 ? value : value * 1000;
    const date = new Date(asMilliseconds);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (/^\d+$/.test(trimmed)) {
      return parseAPITimestamp(Number(trimmed));
    }

    const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(trimmed);
    const normalized = hasTimezone
      ? trimmed
      : `${trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T')}Z`;

    const date = new Date(normalized);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    const fallback = new Date(trimmed.replace(' ', 'T'));
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  if (typeof value.toString === 'function') {
    return parseAPITimestamp(value.toString());
  }

  return null;
};

const computeWeeklyCycle = ({
  currentWeekStart,
  currentWeekEnd,
  nextRefreshAt,
  lastRefreshAt
} = {}) => {
  const now = new Date();

  const parsedLastRefresh = parseAPITimestamp(lastRefreshAt) || parseAPITimestamp(currentWeekStart);
  const parsedWeekStart = parseAPITimestamp(currentWeekStart)
    || (parsedLastRefresh ? new Date(parsedLastRefresh) : new Date(now.getTime() - WEEK_INTERVAL_MS));

  const parsedWeekEnd = parseAPITimestamp(currentWeekEnd)
    || (parsedWeekStart ? new Date(parsedWeekStart.getTime() + WEEK_INTERVAL_MS) : new Date(now.getTime() + WEEK_INTERVAL_MS));

  const parsedNextRefresh = parseAPITimestamp(nextRefreshAt)
    || (parsedWeekEnd ? new Date(parsedWeekEnd) : new Date(parsedWeekStart.getTime() + WEEK_INTERVAL_MS));

  const lastRefresh = parsedLastRefresh && !Number.isNaN(parsedLastRefresh.getTime())
    ? parsedLastRefresh
    : new Date(parsedWeekStart.getTime());

  let nextRefresh = parsedNextRefresh && !Number.isNaN(parsedNextRefresh.getTime())
    ? parsedNextRefresh
    : new Date(parsedWeekEnd.getTime());

  if (nextRefresh <= now) {
    nextRefresh = new Date(nextRefresh.getTime() + WEEK_INTERVAL_MS);
  }

  return {
    lastRefresh,
    nextRefresh,
    weekStart: parsedWeekStart,
    weekEnd: parsedWeekEnd
  };
};

const pickTimestampSource = (entry = {}) => {
  const candidates = [
    entry.timestamp_local,
    entry.timestamp,
    entry.performed_at,
    entry.created_at,
    entry.completed_at,
    entry.date,
    entry.datetime,
    entry.ts,
    entry.time
  ];

  return candidates.find((candidate) => {
    if (candidate === null || candidate === undefined) {
      return false;
    }

    if (candidate instanceof Date) {
      return !Number.isNaN(candidate.getTime());
    }

    if (typeof candidate === 'number') {
      return Number.isFinite(candidate);
    }

    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      return trimmed.length > 0 && trimmed !== '--';
    }

    return true;
  });
};

const resolveTimestamp = (entry = {}) => {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const details = entry.operation_details || entry.operationDetails || {};

  const candidates = [
    pickTimestampSource(entry),
    entry.timestamp,
    entry.timestamp_local,
    entry.timestamp_utc,
    entry.timestampUTC,
    entry.timestampLocal,
    entry.performed_at,
    entry.performedAt,
    entry.completed_at,
    entry.completedAt,
    entry.executed_at,
    entry.executedAt,
    entry.created_at,
    entry.createdAt,
    entry.updated_at,
    entry.updatedAt,
    entry.occurred_at,
    entry.occurredAt,
    entry.happened_at,
    entry.happenedAt,
    entry.recorded_at,
    entry.recordedAt,
    entry.logged_at,
    entry.loggedAt,
    entry.time,
    entry.time_at,
    entry.timeAt,
    entry.ts,
    entry.datetime,
    entry.date,
    entry.time_ms,
    entry.timeMs,
    pickTimestampSource(details),
    details.timestamp,
    details.timestamp_local,
    details.timestamp_utc,
    details.timestampUTC,
    details.timestampLocal,
    details.performed_at,
    details.performedAt,
    details.completed_at,
    details.completedAt,
    details.executed_at,
    details.executedAt,
    details.created_at,
    details.createdAt,
    details.updated_at,
    details.updatedAt,
    details.occurred_at,
    details.occurredAt,
    details.happened_at,
    details.happenedAt,
    details.recorded_at,
    details.recordedAt,
    details.logged_at,
    details.loggedAt,
    details.time,
    details.time_at,
    details.timeAt,
    details.ts,
    details.datetime,
    details.date,
    details.time_ms,
    details.timeMs
  ].filter((candidate) => candidate !== undefined && candidate !== null);

  for (const candidate of candidates) {
    const parsed = parseAPITimestamp(candidate);
    if (parsed) {
      return parsed;
    }
  }

  return null;
};

const extractRelativeTimeLabels = (entry = {}) => {
  if (!entry || typeof entry !== 'object') {
    return [];
  }

  const details = entry.operation_details || entry.operationDetails || {};
  const keys = [
    'relative_time',
    'relativeTime',
    'timeAgo',
    'timeAgoLabel',
    'timestamp_label',
    'timestampLabel',
    'display_time',
    'displayTime',
    'display_timestamp',
    'displayTimestamp',
    'time_label',
    'timeLabel',
    'elapsed',
    'elapsed_time',
    'elapsedTime'
  ];

  const collectValues = (source) => keys
    .map((key) => source[key])
    .filter((value) => typeof value === 'string');

  return [...collectValues(entry), ...collectValues(details)];
};

const extractOperationTokens = (value) => {
  if (!value) return [];

  const normalized = value
    .toString()
    .trim()
    .toLowerCase();

  if (!normalized) {
    return [];
  }

  return normalized
    .split(/[./]/)
    .reduce((acc, segment) => {
      const parts = segment
        .split(/[\s_-]+/)
        .map((token) => token.trim())
        .filter(Boolean);
      return acc.concat(parts);
    }, []);
};

const sanitizeOperationTokens = (tokens) => {
  if (!Array.isArray(tokens)) {
    return [];
  }

  const blacklist = new Set([
    'aibattery',
    'operation',
    'operations',
    'operationtoken',
    'operationtokens',
    'op',
    'token',
    'tokens'
  ]);

  const sanitized = tokens.filter((token) => !blacklist.has(token));
  return Array.from(new Set(sanitized));
};

const normalizePlanLabel = (value) => {
  if (!value) {
    return null;
  }

  const normalized = value.toString().trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  const tokens = normalized.split(/[\s_-]+/).filter(Boolean);
  const base = (tokens[0] || normalized).toLowerCase();
  const map = {
    pro: 'PRO',
    ultra: 'ULTRA',
    free: 'FREE'
  };

  return map[base] || base;
};

const getOperationTokenCount = (details = {}) => {
  if (!details || typeof details !== 'object') {
    return null;
  }

  const total = toNumber(details.total_tokens, NaN);
  if (Number.isFinite(total) && total > 0) {
    return total;
  }

  const input = toNumber(details.input_tokens, NaN);
  const output = toNumber(details.output_tokens, NaN);
  const cached = toNumber(details.cached_input_tokens ?? details.cached_tokens, NaN);

  const sum = [input, output, cached]
    .filter((value) => Number.isFinite(value))
    .reduce((acc, value) => acc + value, 0);

  return sum > 0 ? sum : null;
};

const getOperationImageCount = (details = {}) => {
  if (!details || typeof details !== 'object') {
    return null;
  }

  const count = toNumber(
    details.image_count ?? details.generated_count ?? details.total_images,
    NaN
  );

  if (Number.isFinite(count) && count > 0) {
    return count;
  }

  if (Array.isArray(details.images)) {
    return details.images.length;
  }

  if (Array.isArray(details.variations)) {
    return details.variations.length;
  }

  return null;
};

const DesktopBatteryPage = ({ onNavigate = null }) => {
  const initialCycle = computeWeeklyCycle({
    currentWeekStart: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    currentWeekEnd: new Date(Date.now() + 4 * 24 * 60 * 60 * 1000),
    lastRefreshAt: new Date(Date.now() - 24 * 60 * 60 * 1000)
  });

  const [batteryData, setBatteryData] = useState({
    percentage: 60,
    capacityUnits: 1.5,
    baseCapacityUnits: 1.5,
    remainingUnits: 0.9,
    consumedUnits: 0.6,
    isUpgraded: false,
    lastRecharge: initialCycle.lastRefresh,
    nextRecharge: initialCycle.nextRefresh,
    status: 'active',
    lastUpdated: new Date(),
    weekStart: initialCycle.weekStart,
    weekEnd: initialCycle.weekEnd,
    subscription: null,
    weekKey: null
  });

  const [usageOverview, setUsageOverview] = useState({
    operationCount: null,
    byType: {}
  });

  const [lastUsage, setLastUsage] = useState(null);
  const [batteryHistory, setBatteryHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  const { t, language } = useLanguage();
  const { getNavigationStateForHref, isAccessible } = useCapabilities();

  const integerFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(language || 'it', { maximumFractionDigits: 0 });
    } catch (error) {
      console.warn('[DesktopBatteryPage] Falling back to default integer formatter', error);
      return new Intl.NumberFormat('it', { maximumFractionDigits: 0 });
    }
  }, [language]);

  const decimalFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(language || 'it', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      });
    } catch (error) {
      console.warn('[DesktopBatteryPage] Falling back to default decimal formatter', error);
      return new Intl.NumberFormat('it', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      });
    }
  }, [language]);

  const dateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('[DesktopBatteryPage] Falling back to default date formatter', error);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  }, [language]);

  const dateTimeFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.warn('[DesktopBatteryPage] Falling back to default datetime formatter', error);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }, [language]);

  const formatCount = useCallback((value) => {
    if (value === null || value === undefined) return '--';
    const numericValue = toNumber(value, NaN);
    if (!Number.isFinite(numericValue)) return '--';
    return integerFormatter.format(numericValue);
  }, [integerFormatter]);

  const formatTokens = useCallback((value) => {
    const numericValue = toNumber(value, NaN);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return null;
    }

    const formatted = formatCount(numericValue);
    if (formatted === '--') {
      return null;
    }

    return t('aiBattery.common.tokens', {
      count: numericValue,
      formatted
    });
  }, [formatCount, t]);

  const formatImages = useCallback((value) => {
    const numericValue = toNumber(value, NaN);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return null;
    }

    const formatted = formatCount(numericValue);
    if (formatted === '--') {
      return null;
    }

    return t('aiBattery.common.images', {
      count: numericValue,
      formatted
    });
  }, [formatCount, t]);

  const formatOperationsLabel = useCallback((value) => {
    const numericValue = toNumber(value, NaN);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return '--';
    }

    const formatted = formatCount(numericValue);
    if (formatted === '--') {
      return '--';
    }

    return t('aiBattery.common.operations', {
      count: numericValue,
      formatted
    });
  }, [formatCount, t]);

  const formatUsageDetail = useCallback((usageItem) => {
    const tokensLabel = formatTokens(usageItem?.tokens);
    const imagesLabel = formatImages(usageItem?.images);
    const parts = [tokensLabel, imagesLabel].filter(Boolean);

    if (parts.length > 0) {
      return parts.join(' · ');
    }

    const operationsLabel = formatOperationsLabel(usageItem?.count);
    if (operationsLabel && operationsLabel !== '--') {
      return operationsLabel;
    }

    return t('aiBattery.desktop.usageDetailEmpty', {
      defaultValue: 'Dettagli non disponibili'
    });
  }, [formatImages, formatOperationsLabel, formatTokens, t]);

  const translateOperationToken = useCallback((token) => {
    if (!token) {
      return '';
    }

    const lower = token.toLowerCase();
    if (lower === 'ai') {
      return 'AI';
    }

    const translationKey = `aiBattery.operationTokens.${lower}`;
    const translated = t(translationKey, { defaultValue: '' }).trim();
    if (
      translated
      && translated !== translationKey
      && !translated.startsWith('aiBattery.operationTokens.')
    ) {
      return translated;
    }

    if (lower.length <= 2) {
      return lower.toUpperCase();
    }

    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }, [t]);

  const getActionNameFromType = useCallback((operationType, metadata = {}) => {
    const details = metadata.operation_details || metadata.operationDetails || {};

    const labelCandidate = [
      metadata.operation_label,
      metadata.operation_name,
      metadata.display_name,
      metadata.name,
      metadata.title,
      metadata.label,
      metadata.operation,
      metadata.event,
      metadata.action_label,
      metadata.action_name,
      metadata.description,
      details.display_name,
      details.ui_label,
      details.title,
      details.label,
      details.name,
      details.action_label,
      details.operation,
      details.action,
      details.event
    ].find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);

    if (labelCandidate) {
      return labelCandidate.trim();
    }

    const normalizedType = (operationType || metadata.operation_type || '').toString().trim().toLowerCase();
    if (!normalizedType) {
      return t('aiBattery.fallbackOperation');
    }

    const rawTokens = extractOperationTokens(normalizedType);
    const tokens = sanitizeOperationTokens(rawTokens);

    const candidateKeys = [
      normalizedType,
      normalizedType.replace(/[./]/g, '_'),
      tokens.join('_'),
      tokens[tokens.length - 1]
    ].filter(Boolean);

    for (const key of candidateKeys) {
      const translationKey = `aiBattery.operations.${key}`;
      const translatedType = t(translationKey, { defaultValue: '' }).trim();
      if (
        translatedType
        && translatedType !== translationKey
        && !translatedType.startsWith('aiBattery.operations.')
      ) {
        return translatedType;
      }
    }

    if (normalizedType === 'weekly_refresh') {
      return t('aiBattery.operations.weeklyRefresh', {
        defaultValue: 'Refill settimanale'
      });
    }

    const translatedTokens = tokens.map((word) => translateOperationToken(word)).filter(Boolean);
    if (translatedTokens.length > 0) {
      return translatedTokens.join(' ');
    }

    const fallbackTokens = rawTokens.map((word) => translateOperationToken(word)).filter(Boolean);
    if (fallbackTokens.length > 0) {
      return fallbackTokens.join(' ');
    }

    return t('aiBattery.fallbackOperation');
  }, [t, translateOperationToken]);

  const formatTimestamp = useCallback((timestamp) => {
    const ts = parseAPITimestamp(timestamp);
    if (!ts) return '--';

    const now = new Date();
    const diff = now - ts;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return t('aiBattery.time.seconds');
    if (minutes < 60) return t('aiBattery.time.minutes', { count: minutes });
    if (hours < 24) return t('aiBattery.time.hours', { count: hours });
    if (days === 1) return t('aiBattery.time.yesterday');
    if (days < 7) return t('aiBattery.time.days', { count: days });

    return dateFormatter.format(ts);
  }, [dateFormatter, t]);

  const formatShortDate = useCallback((timestamp) => {
    const ts = parseAPITimestamp(timestamp);
    if (!ts) return '--';

    return dateTimeFormatter.format(ts);
  }, [dateTimeFormatter]);

  const computeHistoryDetail = useCallback((entry) => {
    const { type, operationDetails, amount, movementType } = entry || {};
    if (!operationDetails || typeof operationDetails !== 'object') {
      if ((type || '').toLowerCase() === 'weekly_refresh' || (movementType || '').toLowerCase() === 'credit') {
        return t('aiBattery.history.detail.weeklyRefresh', {
          defaultValue: 'Refill settimanale'
        });
      }
      return null;
    }

    const normalizedType = (type || '').toLowerCase();

    if (
      normalizedType === 'strategy_generation' ||
      normalizedType === 'strategy_week_regeneration' ||
      normalizedType === 'post_generation'
    ) {
      const model = operationDetails.model_used || operationDetails.model || '';
      const totalTokens = operationDetails.total_tokens
        ?? (toNumber(operationDetails.input_tokens, 0) + toNumber(operationDetails.output_tokens, 0));

      const tokensLabel = formatTokens(totalTokens);
      if (model && tokensLabel) {
        return t('aiBattery.history.detail.modelWithTokens', { model, tokens: tokensLabel });
      }
      return model || tokensLabel;
    }

    if (
      normalizedType === 'image_generation' ||
      normalizedType === 'campaign_image_generation' ||
      normalizedType === 'enhanced_image_generation'
    ) {
      const count = operationDetails.image_count
        ?? operationDetails.generated_count
        ?? operationDetails.total_images
        ?? (Array.isArray(operationDetails.images) ? operationDetails.images.length : null);
      const ratio = operationDetails.ratio || operationDetails.image_ratio;

      const parts = [];
      const countLabel = formatImages(count);
      if (countLabel) {
        parts.push(countLabel);
      }
      if (ratio) {
        parts.push(t('aiBattery.history.detail.ratio', { ratio }));
      }

      return parts.join(' - ') || null;
    }

    if (normalizedType === 'analysis') {
      const analyst = (operationDetails.operation && operationDetails.operation.trim().length > 0)
        ? operationDetails.operation
        : t('aiBattery.history.detail.analysisDefault');

      const tokensLabel = formatTokens(operationDetails.total_tokens ?? operationDetails.input_tokens);
      if (tokensLabel) {
        return t('aiBattery.history.detail.analysisWithTokens', {
          analyst,
          tokens: tokensLabel
        });
      }
      return analyst;
    }

    if (normalizedType === 'credit_addition') {
      return operationDetails.reason || t('aiBattery.history.detail.creditAddition');
    }

    if (normalizedType === 'weekly_refresh') {
      const planCode = operationDetails.plan_code;
      const planName = operationDetails.plan_name || planCode;
      const planLabel = normalizePlanLabel(planName);

      if (planLabel) {
        return t('aiBattery.history.detail.weeklyRefreshPlan', {
          plan: planLabel,
          defaultValue: `Ricarica settimanale piano ${planLabel}`
        });
      }

      return t('aiBattery.history.detail.weeklyRefresh', {
        defaultValue: 'Refill settimanale'
      });
    }

    return null;
  }, [formatImages, formatTokens, t]);

  const setDefaultBatteryData = useCallback(() => {
    const now = Date.now();
    const weeklyQuota = 1.5;
    const remainingCredits = 0.9;
    const consumedCredits = weeklyQuota - remainingCredits;
    const defaultCycle = computeWeeklyCycle({
      currentWeekStart: new Date(now - 3 * 24 * 60 * 60 * 1000),
      currentWeekEnd: new Date(now + 4 * 24 * 60 * 60 * 1000),
      lastRefreshAt: new Date(now - 2 * 24 * 60 * 60 * 1000)
    });

    setBatteryData({
      percentage: Math.max(0, Math.min(100, Math.round((remainingCredits / weeklyQuota) * 100))),
      capacityUnits: weeklyQuota,
      baseCapacityUnits: weeklyQuota,
      remainingUnits: remainingCredits,
      consumedUnits: consumedCredits,
      isUpgraded: false,
      lastRecharge: defaultCycle.lastRefresh,
      nextRecharge: defaultCycle.nextRefresh,
      status: 'active',
      lastUpdated: new Date(),
      weekStart: defaultCycle.weekStart,
      weekEnd: defaultCycle.weekEnd,
      subscription: null,
      weekKey: null
    });

    setUsageOverview({
      operationCount: 2,
      byType: {
        strategy_generation: { count: 1, tokens: 2500 },
        image_generation: { count: 1, images: 2 }
      }
    });

    setLastUsage({
      timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
      type: 'debit',
      operation_type: 'strategy_generation',
      amount: consumedCredits / 2,
      operation_details: {
        model_used: 'gpt-5-mini',
        total_tokens: 2500
      },
      balance_after: remainingCredits
    });
  }, []);

  const setDefaultBatteryHistory = useCallback(() => {
    const now = Date.now();
    setBatteryHistory([
      {
        id: 1,
        action: getActionNameFromType('post_generation'),
        timestamp: new Date(now - 1000 * 60 * 30),
        type: 'post_generation',
        operationDetails: {
          model_used: 'GPT-5 mini',
          total_tokens: 1200
        }
      },
      {
        id: 2,
        action: getActionNameFromType('strategy_week_regeneration'),
        timestamp: new Date(now - 1000 * 60 * 60 * 2),
        type: 'strategy_week_regeneration',
        operationDetails: {
          model_used: 'GPT-5 mini',
          total_tokens: 1800
        }
      },
      {
        id: 3,
        action: getActionNameFromType('analysis'),
        timestamp: new Date(now - 1000 * 60 * 60 * 5),
        type: 'analysis',
        operationDetails: {
          total_tokens: 1400
        }
      }
    ]);
  }, [getActionNameFromType]);

  const fetchBatteryStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiFetch(`${API_CONFIG.BASE_URL}/api/v1/user/ai-battery`, {
        method: 'GET'
      });

      if (!response.ok) {
        console.error('[DesktopBatteryPage] Failed to fetch battery status:', response.status);
        setDefaultBatteryData();
        return;
      }

      const data = await parseResponseBody(response);
      const apiBattery = data.ai_battery || {};
      const subscriptionInfo = apiBattery.subscription || {};

      const weeklyQuota = toNumber(apiBattery.weekly_quota, NaN);
      const remainingCredits = toNumber(apiBattery.remaining_credits, NaN);
      const subscriptionWeeklyCredits = toNumber(subscriptionInfo.weekly_credits, NaN);
      const consumedCredits = toNumber(
        apiBattery.consumed_credits,
        Number.isFinite(weeklyQuota) && Number.isFinite(remainingCredits)
          ? weeklyQuota - remainingCredits
          : NaN
      );

      const cycle = computeWeeklyCycle({
        currentWeekStart: apiBattery.current_week_start,
        currentWeekEnd: apiBattery.current_week_end,
        nextRefreshAt: subscriptionInfo.next_refresh_at || apiBattery.next_refresh_at,
        lastRefreshAt: apiBattery.last_refresh_at || subscriptionInfo.last_refresh_at
      });

      const safeWeeklyQuota = Number.isFinite(weeklyQuota) && weeklyQuota > 0
        ? weeklyQuota
        : (Number.isFinite(subscriptionWeeklyCredits) && subscriptionWeeklyCredits > 0
          ? subscriptionWeeklyCredits
          : (Number.isFinite(remainingCredits) && remainingCredits > 0 ? remainingCredits : 1));

      const safeRemaining = Number.isFinite(remainingCredits)
        ? Math.max(0, remainingCredits)
        : safeWeeklyQuota;

      const derivedConsumed = Number.isFinite(consumedCredits)
        ? Math.max(0, consumedCredits)
        : Math.max(0, safeWeeklyQuota - safeRemaining);

      const consumedPercentage = toNumber(apiBattery.consumed_percentage, NaN);
      const percentageRemaining = safeWeeklyQuota > 0
        ? Math.max(0, Math.min(100, Math.round((safeRemaining / safeWeeklyQuota) * 100)))
        : (Number.isFinite(consumedPercentage) ? Math.max(0, Math.min(100, Math.round(100 - consumedPercentage))) : 0);

      const planCode = (subscriptionInfo.plan_code || '').toString().toLowerCase();
      const isUpgraded = safeWeeklyQuota > 1 || (planCode && planCode !== 'free_trial');

      setBatteryData({
        percentage: percentageRemaining,
        capacityUnits: safeWeeklyQuota,
        baseCapacityUnits: Number.isFinite(subscriptionWeeklyCredits)
          ? subscriptionWeeklyCredits
          : safeWeeklyQuota,
        remainingUnits: safeRemaining,
        consumedUnits: derivedConsumed,
        isUpgraded,
        lastRecharge: cycle.lastRefresh,
        nextRecharge: cycle.nextRefresh,
        status: apiBattery.status || (safeRemaining <= 0 ? 'depleted' : 'active'),
        lastUpdated: new Date(),
        weekStart: cycle.weekStart,
        weekEnd: cycle.weekEnd,
        subscription: subscriptionInfo,
        weekKey: apiBattery.week_key
      });

      const recentActivity = apiBattery.recent_activity || {};
      const recentTransactions = Array.isArray(recentActivity.transactions)
        ? recentActivity.transactions
        : [];
      const debitTransactions = recentTransactions.filter((tx) => (tx?.type || '').toLowerCase() === 'debit');

      const byType = debitTransactions.reduce((acc, tx) => {
        const type = tx?.operation_type || 'unknown';
        const current = acc[type] || { count: 0, tokens: 0, images: 0 };
        current.count += 1;
        const details = tx?.operation_details || {};
        const tokenCount = getOperationTokenCount(details);
        if (tokenCount) {
          current.tokens += tokenCount;
        }
        const imageCount = getOperationImageCount(details);
        if (imageCount) {
          current.images += imageCount;
        }
        acc[type] = current;
        return acc;
      }, {});

      setUsageOverview({
        operationCount: toNumber(
          recentActivity.operation_count,
          toNumber(recentActivity.transaction_count, debitTransactions.length)
        ),
        byType
      });

      const lastTransaction = recentActivity.last_transaction
        || debitTransactions[0]
        || recentTransactions[0]
        || null;
      setLastUsage(lastTransaction ? { ...lastTransaction } : null);

      console.log('[DesktopBatteryPage] Battery status updated (weekly model):', data);
    } catch (error) {
      console.error('[DesktopBatteryPage] Error fetching battery status:', error);
      setDefaultBatteryData();
    } finally {
      setIsLoading(false);
    }
  }, [setDefaultBatteryData]);

  const fetchBatteryHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    try {
      const response = await apiFetch(`${API_CONFIG.BASE_URL}/api/v1/user/ai-battery/usage-history?limit=12&offset=0`, {
        method: 'GET'
      });

      if (!response.ok) {
        console.error('[DesktopBatteryPage] Failed to fetch battery history:', response.status);
        setDefaultBatteryHistory();
        return;
      }

      const data = await parseResponseBody(response);
      const transactions = Array.isArray(data.transactions) ? data.transactions : [];
      const formattedHistory = transactions.map((item, index) => {
        const timestampSource = pickTimestampSource(item) || item.created_at;
        const timestamp = parseAPITimestamp(timestampSource) || new Date();

        return {
          id: item.id || `${timestamp.getTime()}-${index}`,
          action: getActionNameFromType(item.operation_type, item),
          timestamp,
          type: item.operation_type,
          operationDetails: item.operation_details,
          amount: item.amount,
          balance_after: item.balance_after,
          movementType: item.type
        };
      });

      setBatteryHistory(formattedHistory);
      console.log('[DesktopBatteryPage] Battery history updated:', formattedHistory);
    } catch (error) {
      console.error('[DesktopBatteryPage] Error fetching battery history:', error);
      setDefaultBatteryHistory();
    } finally {
      setIsHistoryLoading(false);
    }
  }, [getActionNameFromType, setDefaultBatteryHistory]);

  useEffect(() => {
    let isMounted = true;

    const loadBatteryData = async () => {
      await Promise.all([fetchBatteryStatus(), fetchBatteryHistory()]);
    };

    loadBatteryData();

    const refreshInterval = setInterval(() => {
      if (!isMounted) return;
      fetchBatteryStatus();
      fetchBatteryHistory();
    }, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
    };
  }, [fetchBatteryHistory, fetchBatteryStatus]);

  const batteryStatusClass = useMemo(() => {
    if (batteryData.percentage < 20) return 'low';
    if (batteryData.percentage < 50) return 'medium';
    return 'high';
  }, [batteryData.percentage]);

  const usageByType = useMemo(() => {
    const entries = usageOverview.byType || {};
    return Object.entries(entries)
      .map(([type, info]) => ({
        type,
        count: toNumber(info?.count, null),
        tokens: toNumber(info?.tokens, null),
        images: toNumber(info?.images, null)
      }))
      .sort((a, b) => (toNumber(b.count, 0) - toNumber(a.count, 0)));
  }, [usageOverview.byType]);

  const lastUsageSummary = useMemo(() => {
    if (!lastUsage) {
      return t('aiBattery.stats.lastActivity.empty');
    }

    const actionName = getActionNameFromType(lastUsage.operation_type, lastUsage);
    const resolvedTimestamp = resolveTimestamp(lastUsage);

    let timestampText = resolvedTimestamp ? formatTimestamp(resolvedTimestamp) : '--';

    if (!timestampText || timestampText === '--') {
      const fallbackTime = extractRelativeTimeLabels(lastUsage).find((value) => {
        if (typeof value !== 'string') {
          return false;
        }

        const trimmed = value.trim();
        return trimmed.length > 0 && trimmed !== '--';
      });

      if (fallbackTime) {
        timestampText = fallbackTime.trim();
      }
    }

    if (!actionName && !timestampText) {
      return t('aiBattery.stats.lastActivity.empty');
    }

    return t('aiBattery.stats.lastActivity.value', {
      action: actionName,
      time: timestampText
    });
  }, [formatTimestamp, getActionNameFromType, lastUsage, t]);

  const formattedNextRecharge = useMemo(() => (
    batteryData.nextRecharge ? formatShortDate(batteryData.nextRecharge) : '--'
  ), [batteryData.nextRecharge, formatShortDate]);

  const capacityLabel = useMemo(() => {
    if (!Number.isFinite(batteryData.capacityUnits)) {
      return '--';
    }

    const formattedQuota = decimalFormatter.format(batteryData.capacityUnits);
    const planName = batteryData.subscription?.plan_name
      || batteryData.subscription?.plan_code
      || null;

    if (planName) {
      return t('aiBattery.stats.capacity.plan', {
        plan: planName,
        amount: formattedQuota,
        defaultValue: `${planName} - ${formattedQuota}`
      });
    }

    if (!batteryData.baseCapacityUnits) {
      return formattedQuota;
    }

    const multiplier = batteryData.capacityUnits / batteryData.baseCapacityUnits;
    if (multiplier <= 1.01) {
      return t('aiBattery.stats.capacity.standard', {
        amount: formattedQuota,
        defaultValue: formattedQuota
      });
    }

    const formattedMultiplier = decimalFormatter.format(multiplier);
    return t('aiBattery.stats.capacity.upgraded', {
      multiplier: formattedMultiplier,
      amount: formattedQuota,
      defaultValue: `${formattedMultiplier}x - ${formattedQuota}`
    });
  }, [batteryData.baseCapacityUnits, batteryData.capacityUnits, batteryData.subscription, decimalFormatter, t]);

  const getStatusLabel = useCallback(() => {
    const status = (batteryData.status || '').toLowerCase();
    if (status === 'depleted') return t('aiBattery.status.depleted', { defaultValue: 'Scarica' });
    if (status === 'low') return t('aiBattery.status.low', { defaultValue: 'Bassa' });
    if (status === 'refreshing' || status === 'charging') {
      return t('aiBattery.status.charging', { defaultValue: 'Ricarica' });
    }
    return t('aiBattery.status.active', { defaultValue: 'Attiva' });
  }, [batteryData.status, t]);

  const renderSummarySkeleton = () => (
    <div className="loopino-battery-layout">
      <div className="loopino-gradient-border">
        <div className="loopino-card loopino-battery-hero">
          <div className="loopino-battery-meter">
            <Skeleton variant="rounded" width="100%" height={240} shimmer />
          </div>
          <div className="loopino-battery-hero-content">
            <Skeleton width="60%" height={28} shimmer />
            <Skeleton width="50%" height={16} shimmer />
            <div className="loopino-stat-grid">
              {Array.from({ length: 2 }).map((_, idx) => (
                <div className="loopino-stat" key={`stat-skel-${idx}`}>
                  <Skeleton width="40%" height={12} shimmer />
                  <Skeleton width="70%" height={22} shimmer />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="loopino-glass-panel loopino-battery-history">
        <div className="loopino-battery-history-header">
          <Skeleton width="40%" height={18} shimmer />
          <Skeleton width="20%" height={16} shimmer />
        </div>
        <div className="loopino-battery-history-list">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div className="loopino-battery-history-item" key={`history-skel-${idx}`}>
              <div className="loopino-battery-history-icon">
                <Skeleton variant="circle" width={40} height={40} shimmer />
              </div>
              <div className="loopino-battery-history-meta">
                <Skeleton width="60%" height={16} shimmer />
                <Skeleton width="40%" height={12} shimmer />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const weeklyWindow = useMemo(() => {
    if (!batteryData.weekStart || !batteryData.weekEnd) return '--';
    return `${dateFormatter.format(batteryData.weekStart)} → ${dateFormatter.format(batteryData.weekEnd)}`;
  }, [batteryData.weekEnd, batteryData.weekStart, dateFormatter]);
  const subscriptionCapability = getNavigationStateForHref('/desktop/subscription');
  const canOpenSubscription = !subscriptionCapability.managed || isAccessible(subscriptionCapability);

  const handleManualRefresh = () => {
    fetchBatteryStatus();
    fetchBatteryHistory();
  };

  const handleHeroNavigation = useCallback(() => {
    if (!canOpenSubscription) {
      return;
    }
    if (typeof onNavigate === 'function') {
      onNavigate('/desktop/subscription');
      return;
    }

    if (typeof window !== 'undefined') {
      window.location.href = '/desktop/subscription';
    }
  }, [canOpenSubscription, onNavigate]);

  const handleHeroKeyDown = useCallback((event) => {
    if (!event) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleHeroNavigation();
    }
  }, [handleHeroNavigation]);

  return (
    <div className="loopino-desktop-shell loopino-desktop-battery-shell">
      <header className="loopino-desktop-battery-header">
        <div>
          <p className="loopino-desktop-battery-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <h1>{t('aiBattery.desktop.title', { defaultValue: 'Pannello batteria AI' })}</h1>
          <p className="loopino-desktop-battery-summary">
            {t('aiBattery.desktop.subtitle', {
              defaultValue: 'Gestisci i crediti AI settimanali, controlla le ultime operazioni e prevedi il prossimo refill.'
            })}
          </p>
        </div>
      </header>

      {isLoading ? renderSummarySkeleton() : (
        <div className="loopino-battery-layout">
          <div className="loopino-gradient-border">
            <div
              className="loopino-card loopino-battery-hero"
              role={canOpenSubscription ? 'button' : undefined}
              tabIndex={canOpenSubscription ? 0 : undefined}
              onClick={canOpenSubscription ? handleHeroNavigation : undefined}
              onKeyDown={canOpenSubscription ? handleHeroKeyDown : undefined}
            >
              <div className={`loopino-battery-meter ${batteryStatusClass}`}>
                <div
                  className="loopino-battery-meter-fill"
                  style={{ '--battery-percentage': `${batteryData.percentage}%` }}
                >
                  <Iridescence
                    className="loopino-battery-iridescence"
                    color={[0.706, 0.477, 0.683]}
                    mouseReact={false}
                    amplitude={0.08}
                    speed={1.0}
                  />
                </div>
                <div className="loopino-battery-meter-outline" />
                <div className="loopino-battery-meter-value">
                  <span>{batteryData.percentage}%</span>
                  <p>{t('aiBattery.header.title')}</p>
                </div>
              </div>

              <div className="loopino-battery-hero-content">
                <div className="loopino-battery-hero-row">
                  <div className="loopino-battery-plan">
                    <p className="loopino-eyebrow">{t('aiBattery.stats.capacity.label')}</p>
                    <h3 className="loopino-gradient-text">{capacityLabel}</h3>
                    <p className="loopino-text-muted">
                      {t('aiBattery.stats.capacity.help')}
                    </p>
                  </div>
                  <div className="loopino-battery-week">
                    <p className="loopino-eyebrow">{t('aiBattery.desktop.weekWindow', { defaultValue: 'Finestra settimanale' })}</p>
                    <h4>{weeklyWindow}</h4>
                    <p className="loopino-text-muted">{t('aiBattery.stats.nextRecharge.help')}</p>
                  </div>
                </div>

                <div className="loopino-stat-grid">
                  <div className="loopino-stat">
                    <span className="loopino-stat-label">{t('aiBattery.stats.nextRecharge.label')}</span>
                    <span className="loopino-stat-value">{formattedNextRecharge}</span>
                    <p className="loopino-text-muted">{t('aiBattery.stats.lastActivity.label')}</p>
                    <p className="loopino-battery-small">{lastUsageSummary}</p>
                  </div>
                  <div className="loopino-stat">
                    <span className="loopino-stat-label">{t('aiBattery.stats.operations.label')}</span>
                    <span className="loopino-stat-value">{formatCount(usageOverview.operationCount)}</span>
                    <p className="loopino-text-muted">
                      {t('aiBattery.desktop.operationsHelp', {
                        defaultValue: t('aiBattery.stats.operations.help')
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="loopino-glass-panel loopino-battery-usage">
            <div className="loopino-battery-usage-header">
              <div>
                <p className="loopino-eyebrow">{t('aiBattery.usage.title')}</p>
                <h3>{t('aiBattery.desktop.usageSubtitle', {
                  defaultValue: t('aiBattery.usage.subtitle')
                })}</h3>
              </div>
              <div className="loopino-battery-chip-row">
                <div className="loopino-chip loopino-battery-chip ghost">
                  <img src={iconAi} alt="" className="loopino-battery-chip-icon" />
                  {t('aiBattery.header.title')}
                </div>
                <div className="loopino-chip loopino-battery-chip ghost">
                  {t('aiBattery.stats.operations.label')}: {formatCount(usageOverview.operationCount)}
                </div>
              </div>
            </div>
            <div className="loopino-battery-usage-chips">
              {usageByType.length === 0 && (
                <div className="loopino-battery-empty">
                  {t('aiBattery.stats.lastActivity.empty')}
                </div>
              )}
              {usageByType.map((item) => (
                <div key={item.type} className="loopino-battery-usage-chip">
                  <div className="loopino-battery-usage-chip-top">
                    <span className="loopino-battery-chip-title">{getActionNameFromType(item.type)}</span>
                    <span className="loopino-battery-chip-count">
                      {formatOperationsLabel(item.count)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="loopino-glass-panel loopino-battery-history">
            <div className="loopino-battery-history-header">
              <div>
                <p className="loopino-eyebrow">{t('aiBattery.history.title')}</p>
                <h3>{t('aiBattery.desktop.historySubtitle', { defaultValue: 'Ultime operazioni AI' })}</h3>
              </div>
              <div className="loopino-chip loopino-battery-chip ghost">
                {batteryData.weekKey || t('aiBattery.desktop.weekKeyFallback', { defaultValue: 'Settimana corrente' })}
              </div>
            </div>

            <div className="loopino-battery-history-list">
              {isHistoryLoading && batteryHistory.length === 0 ? (
                Array.from({ length: 4 }).map((_, idx) => (
                  <div className="loopino-battery-history-item" key={`history-skeleton-${idx}`}>
                    <div className="loopino-battery-history-icon">
                      <Skeleton variant="circle" width={40} height={40} shimmer />
                    </div>
                    <div className="loopino-battery-history-meta">
                      <Skeleton width="60%" height={16} shimmer />
                      <Skeleton width="40%" height={12} shimmer />
                    </div>
                  </div>
                ))
              ) : (
                batteryHistory.map((item) => {
                  const detail = computeHistoryDetail(item);
                  return (
                    <div key={item.id} className="loopino-battery-history-item">
                      <div className="loopino-battery-history-icon">
                        <img src={iconAi} alt={t('aiBattery.alt.ai')} />
                      </div>
                      <div className="loopino-battery-history-meta">
                        <div className="loopino-battery-history-title-row">
                          <span className="loopino-battery-history-action">{item.action}</span>
                        </div>
                        <div className="loopino-battery-history-timestamp">
                          <img src={iconCalendar} alt={t('aiBattery.alt.calendar')} className="loopino-battery-history-icon-small" />
                          {formatTimestamp(item.timestamp)}
                        </div>
                        {detail && (
                          <div className="loopino-battery-history-detail">{detail}</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopBatteryPage;
